import { oc } from '@orpc/contract';
import * as z from 'zod';
import { devicePlatformSchema, deviceSchema, pairingCodeSchema, pairingStatusSchema, } from '../domain/device.js';
import { childIdSchema, deviceIdSchema } from '../primitives/id.js';
import { instantSchema } from '../primitives/time.js';
import { API_VERSION } from './version.js';
const devices = `/${API_VERSION}/devices`;
const pairing = `/${API_VERSION}/pairing`;
/**
 * Pairing, from the tablet's side. **Unauthenticated by necessity.**
 *
 * A child's device has no credentials before it is paired, and SPEC.md §19
 * forbids putting a parent login on it. So these two endpoints are open, and
 * their safety comes from elsewhere: the code is single-use, short-lived, and
 * worth nothing without a parent claiming it from an authenticated session.
 * Both endpoints must be rate-limited.
 */
export const pairingContract = {
    start: oc
        .route({ method: 'POST', path: `${pairing}/start` })
        .input(z.object({
        platform: devicePlatformSchema,
        /** Shown to the parent while choosing, e.g. "Galaxy Tab A9". */
        deviceName: z.string().min(1).max(60),
    }))
        .output(z.object({
        pairingCode: pairingCodeSchema,
        expiresAt: instantSchema,
    })),
    /**
     * Polled by the tablet until a parent claims the code.
     *
     * The device token is returned exactly once, at the moment of transition to
     * `CLAIMED`. It is scoped to this device and this family, and can be revoked
     * from the parent app without touching any other device.
     */
    status: oc
        .route({ method: 'GET', path: `${pairing}/{pairingCode}` })
        .input(z.object({ pairingCode: pairingCodeSchema }))
        .output(z.object({
        status: pairingStatusSchema,
        deviceId: deviceIdSchema.nullable(),
        childId: childIdSchema.nullable(),
        deviceToken: z.string().nullable(),
    }))
        .errors({ PAIRING_NOT_FOUND: { status: 404, message: 'Pairing code is unknown or expired' } }),
};
/** Device management, from the parent's side. Authenticated. */
export const devicesContract = {
    list: oc
        .route({ method: 'GET', path: devices })
        .output(z.object({ devices: z.array(deviceSchema) })),
    claimPairing: oc
        .route({ method: 'POST', path: `${pairing}/claim` })
        .input(z.object({ pairingCode: pairingCodeSchema, childId: childIdSchema }))
        .output(z.object({ device: deviceSchema }))
        .errors({
        PAIRING_NOT_FOUND: { status: 404, message: 'Pairing code is unknown or expired' },
        PAIRING_ALREADY_CLAIMED: { status: 409, message: 'Pairing code has already been used' },
        CHILD_NOT_FOUND: { status: 404, message: 'Child not found' },
        DEVICE_LIMIT_REACHED: { status: 402, message: 'Subscription does not allow another device' },
    }),
    /**
     * Switches which child the device is running.
     *
     * Behind the Parent Gate. Without that a child moves to a sibling's profile
     * and inherits their budget (SPEC.md §19.1).
     */
    setActiveChild: oc
        .route({ method: 'POST', path: `${devices}/{deviceId}/active-child` })
        .input(z.object({ deviceId: deviceIdSchema, childId: childIdSchema }))
        .output(z.object({ device: deviceSchema }))
        .errors({
        DEVICE_NOT_FOUND: { status: 404, message: 'Device not found' },
        CHILD_NOT_ASSIGNED: { status: 409, message: 'Child is not assigned to this device' },
    }),
    /** Invalidates the device token immediately. Used for a lost or sold tablet. */
    revoke: oc
        .route({ method: 'DELETE', path: `${devices}/{deviceId}` })
        .input(z.object({ deviceId: deviceIdSchema }))
        .output(z.object({ success: z.literal(true) }))
        .errors({ DEVICE_NOT_FOUND: { status: 404, message: 'Device not found' } }),
};
//# sourceMappingURL=devices.js.map